import { shortDate } from '@app/utils/formatters';

type EntityList = Record<string, { name?: string } | undefined> | undefined;

const pushRow = (rows: string[][], label: string, value: string | null) => {
    const v = (value ?? '').trim();
    if (!v) return;
    rows.push([label, v]);
};

const formatListSelection = (
    list: EntityList,
    selected: string[] | undefined,
    allLabel: string,
): string | null => {
    if (!selected?.length) return null;
    const keys = list ? Object.keys(list) : [];
    if (keys.length > 0 && selected.length >= keys.length) {
        return allLabel;
    }
    const parts = selected.map(id => list?.[id]?.name ?? id).filter(Boolean);
    const joined = parts.join('; ');
    return joined || null;
};

const formatGroupByLabels = (
    groupBy: string[] | undefined,
    t: (key: string, o?: { ns?: string; defaultValue?: string }) => string,
): string | null => {
    if (!groupBy?.length) return null;
    const labels = groupBy.map(dim =>
        t(`groupByOptions.${dim}`, {
            ns: 'common',
            defaultValue: dim,
        }),
    );
    return labels.join(', ');
};

const formatAuthorshipTypes = (
    types: string[] | undefined,
    t: (key: string, o?: { ns?: string }) => string,
): string | null => {
    if (!Array.isArray(types) || types.length === 0 || types.length >= 3) {
        return null;
    }
    const labels = types.map(type =>
        t(`filters.authorshipType.${type}`, { ns: 'common' }),
    );
    return labels.join('; ');
};

export type BuildInsightsCsvFilterRowsParams = {
    filters: any;
    query: { groupBy?: string[] };
    hasCompareDates: boolean;
    t: (
        key: string,
        options?: { ns?: string; defaultValue?: string },
    ) => string;
};

export const buildInsightsCsvFilterRows = ({
    filters,
    query,
    hasCompareDates,
    t,
}: BuildInsightsCsvFilterRowsParams): string[][] => {
    const rows: string[][] = [];
    const allLabel = t('dataExplorer.csvExport.allSelected', {
        ns: 'measurement',
    });

    if (!filters?.startDate || !filters?.endDate) {
        rows.push([
            t('dataExplorer.csvExport.filtersLabel', { ns: 'measurement' }),
            t('dataExplorer.csvExport.filtersNone', { ns: 'measurement' }),
        ]);
        return rows;
    }

    pushRow(
        rows,
        t('dataExplorer.csvExport.primaryDateRange', { ns: 'measurement' }),
        `${shortDate(filters.startDate)} - ${shortDate(filters.endDate)}`,
    );

    if (
        hasCompareDates &&
        filters.compareDates?.dates?.startDate &&
        filters.compareDates?.dates?.endDate
    ) {
        pushRow(
            rows,
            t('dataExplorer.csvExport.compareDateRange', { ns: 'measurement' }),
            `${shortDate(filters.compareDates.dates.startDate)} - ${shortDate(
                filters.compareDates.dates.endDate,
            )}`,
        );
    }

    pushRow(
        rows,
        t('filters.groupBy', { ns: 'common' }),
        formatGroupByLabels(query?.groupBy, t),
    );

    pushRow(
        rows,
        t('filters.rightsholders', { ns: 'common' }),
        formatListSelection(
            filters.rightsholders?.list,
            filters.rightsholders?.selected,
            allLabel,
        ),
    );

    pushRow(
        rows,
        t('filters.sponsors', { ns: 'common' }),
        formatListSelection(
            filters.sponsors?.list,
            filters.sponsors?.selected,
            allLabel,
        ),
    );

    pushRow(
        rows,
        t('filters.placements', { ns: 'common' }),
        formatListSelection(
            filters.placements?.list,
            filters.placements?.selected,
            allLabel,
        ),
    );

    pushRow(
        rows,
        t('filters.placementType', { ns: 'common' }),
        formatListSelection(
            filters.placementTypes?.list,
            filters.placementTypes?.selected,
            allLabel,
        ),
    );

    pushRow(
        rows,
        t('filters.platforms', { ns: 'common' }),
        formatListSelection(
            filters.platformPostTypePairs?.list,
            filters.platformPostTypePairs?.selected,
            allLabel,
        ),
    );

    pushRow(
        rows,
        t('filters.accountTypes', { ns: 'common' }),
        formatListSelection(
            filters.accOwnTypes?.list,
            filters.accOwnTypes?.selected,
            allLabel,
        ),
    );

    pushRow(
        rows,
        t('filters.mediaTypes', { ns: 'common' }),
        formatListSelection(
            filters.mediaTypes?.list,
            filters.mediaTypes?.selected,
            allLabel,
        ),
    );

    pushRow(
        rows,
        t('filters.collections', { ns: 'common' }),
        formatListSelection(
            filters.collections?.list,
            filters.collections?.selected,
            allLabel,
        ),
    );

    pushRow(
        rows,
        t('filters.countries', { ns: 'common' }),
        formatListSelection(
            filters.countries?.list,
            filters.countries?.selected,
            allLabel,
        ),
    );

    pushRow(
        rows,
        t('filters.broadcastNetworks', { ns: 'common' }),
        formatListSelection(
            filters.broadcastNetworks?.list,
            filters.broadcastNetworks?.selected,
            allLabel,
        ),
    );

    const handles = filters.accountUsernames;
    if (Array.isArray(handles) && handles.length > 0) {
        pushRow(
            rows,
            t('filters.handles', { ns: 'common' }),
            handles.join('; '),
        );
    }

    const hashtags = filters.hashtags;
    if (Array.isArray(hashtags) && hashtags.length > 0) {
        pushRow(
            rows,
            t('filters.hashtags', { ns: 'common' }),
            hashtags.join('; '),
        );
    }

    const networks = filters.networks;
    if (Array.isArray(networks) && networks.length > 0) {
        pushRow(
            rows,
            t('filters.network', { ns: 'common' }),
            networks.join('; '),
        );
    }

    pushRow(
        rows,
        t('filters.authorshipType.label', { ns: 'common' }),
        formatAuthorshipTypes(filters.authorshipTypes, t),
    );

    const collab = filters.collaboratingAccountUsernames;
    if (Array.isArray(collab) && collab.length > 0) {
        pushRow(
            rows,
            t('filters.collaboratingAccounts', { ns: 'common' }),
            collab.join('; '),
        );
    }

    const mvp = Number(filters.mvpScore);
    if (!Number.isNaN(mvp) && mvp > 0) {
        pushRow(rows, t('filters.mvpScore', { ns: 'common' }), String(mvp));
    }

    const bNames = filters.broadcastNames;
    if (Array.isArray(bNames) && bNames.length > 0) {
        pushRow(
            rows,
            t('filters.broadcastNames', { ns: 'common' }),
            bNames.join('; '),
        );
    }

    const bOptions = filters.broadcastOptions;
    if (Array.isArray(bOptions) && bOptions.length > 0) {
        pushRow(
            rows,
            t('filters.broadcastName', { ns: 'common' }),
            bOptions.join('; '),
        );
    }

    return rows;
};
